// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { miniMyAccountStyles } from "@ces/App/Components/MiniMyAccount/MiniMyAccount";
import { constants } from "@ces/XDL/constants";
import ChangeShipTo from "@ces/XDL/Icons/ChangeShipTo";
import LinkArrowRight from "@ces/XDL/Icons/LinkArrowRight";
import Logout from "@ces/XDL/Icons/Logout";
import { fontConfig } from "@ces/XDL/PreStyle/typography";
import { color, xScreen } from "@ces/XDL/Utilities";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { css } from "styled-components";

const sharedSectionTitleStyle: TypographyPresentationProps = {
    variant: "h6",
    forwardAs: "span",
    size: 14,
    lineHeight: "22px",
    css: css`
        width: 100%;
    `,
};

miniMyAccountStyles.wrapper = {
    css: css`
        display: flex;
        flex-direction: column;
        ${xScreen("lt", "sm")} {
            height: calc(100vh - ${constants.header.mobile.size.height}px);
            justify-content: space-between;
        }
    `,
};

miniMyAccountStyles.container = {
    css: css`
        padding: 17px 17px 0 17px;
    `,
};

miniMyAccountStyles.titleWrapper = {
    css: css`
        margin-bottom: 12px;
        padding-bottom: 8px;
        border-bottom: 1px solid ${color("common.border")};
    `,
};

miniMyAccountStyles.title = {
    variant: "h5",
    css: css`
        margin-bottom: 0;
    `,
};

miniMyAccountStyles.personalInfo = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: 8px;
            margin-bottom: 24px;
        `,
    },
    infoWrapper: {
        css: css`
            display: flex;
            gap: 16px;
            ${xScreen("lt", "sm")} {
                flex-direction: column;
            }
        `,
    },
    infoColumnContainer: {
        css: css`
            display: flex;
            flex-direction: column;
            flex: 1;
            max-width: 50%;
            ${TypographyStyle} {
                ${wrapInContainerStyles}
            }
        `,
    },
    title: {
        ...sharedSectionTitleStyle,
        css: css`
            margin-bottom: 0;
        `,
    },
    subTitle: {
        size: 12,
        lineHeight: "13px",
        weight: fontConfig.weight.bold,
        css: css`
            margin-bottom: 8px;
        `,
    },
    name: {
        size: 12,
        lineHeight: "13px",
        color: "common.ui6",
        css: css`
            margin-bottom: 4px;
        `,
    },
    label: {
        color: "common.ui6",
        lineHeight: "13px",
        css: css`
            margin-bottom: 4px;
        `,
    },
    accountLink: {
        color: "common.ui6",
        css: css`
            margin-bottom: 4px;
            span {
                line-height: 13px;
            }
        `,
    },
    viewLink: {
        typographyProps: {
            css: css`
                line-height: 13px;
            `,
        },
        css: css`
            margin-top: 4px;
        `,
        icon: {
            position: "right",
            iconProps: {
                size: 12,
                src: LinkArrowRight,
            },
        },
    },
};

miniMyAccountStyles.linkItems = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: 8px;
            margin-bottom: 24px;
        `,
    },
    title: {
        ...sharedSectionTitleStyle,
        css: css`
            margin-bottom: 0;
        `,
    },
    linksContainer: {
        gap: 16,
        offsetCss: css`
            ${xScreen("lt", "sm")} {
                flex-direction: column;
            }
        `,
    },
    linkWrapper: {
        width: 6,
    },
    link: {
        typographyProps: {
            size: 12,
            weight: fontConfig.weight.bold,
            css: css`
                line-height: 13px;
            `,
        },
    },
};

miniMyAccountStyles.actionSection = {
    wrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            padding: 16px;
            border-top: 1px solid ${color("common.border")};
        `,
    },
    changeShipTo: {
        icon: {
            iconProps: { src: ChangeShipTo },
            position: "right",
        },
    },
    signOutButton: {
        icon: {
            iconProps: { src: Logout },
            position: "right",
        },
    },
};
